<template>
  <aside class="flex flex-col bg-surface min-h-0" :aria-label="$t('AI assistant')">
    <!-- Header -->
    <div class="shrink-0 h-14 px-4 flex items-center gap-2 border-b border-line-card">
      <span class="w-7 h-7 rounded-lg bg-up-bg text-brand flex items-center justify-center" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3l1.8 4.6L18.5 9.5l-4.7 1.9L12 16l-1.8-4.6L5.5 9.5l4.7-1.9z" />
          <path d="M19 15l.7 1.8 1.8.7-1.8.7L19 20l-.7-1.8-1.8-.7 1.8-.7z" />
        </svg>
      </span>
      <h2 class="text-[14px] font-semibold text-ink">{{ $t("AI assistant") }}</h2>
      <div class="ml-auto flex items-center gap-1">
        <button v-if="messages.length" type="button" class="qp-btn qp-btn-ghost !px-2 text-[12px]" :disabled="loading"
          :title="$t('assistant-new-search-hint')" @click="reset">
          {{ $t("New search") }}
        </button>
        <button type="button" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-3"
          :aria-label="$t('Close the assistant')" :title="$t('Close the assistant')" @click="$emit('close')">
          <svg width="12" height="12" viewBox="0 0 16 17" fill="none" class="text-muted" aria-hidden="true">
            <line x1="14.7071" y1="1.70711" x2="0.707107" y2="15.7071" stroke="currentColor" stroke-width="2" />
            <line x1="0.707107" y1="1.29289" x2="14.7071" y2="15.2929" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Conversation -->
    <div ref="log" class="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-3" aria-live="polite">
      <div v-if="!messages.length" class="flex flex-col gap-3">
        <p class="text-[13px] leading-5 text-ink-3">{{ $t("assistant-intro") }}</p>
        <p class="text-[12px] font-medium text-faint uppercase tracking-wide">{{ $t("Examples") }}</p>
        <button v-for="example in examples" :key="example" type="button" :disabled="loading"
          class="text-left text-[13px] leading-5 px-3 py-2 rounded-xl border border-line text-ink-2 hover:border-brand hover:bg-brand-tint transition"
          @click="send(example)">
          {{ example }}
        </button>
      </div>

      <template v-for="(m, i) in messages">
        <div v-if="m.role === 'user'" :key="`u${i}`"
          class="self-end max-w-[85%] px-3 py-2 rounded-2xl rounded-br-md bg-inverse text-inverse-ink text-[13px] leading-5 whitespace-pre-wrap break-words">
          {{ m.content }}
        </div>

        <div v-else-if="m.error" :key="`e${i}`"
          class="self-start max-w-[92%] px-3 py-2 rounded-2xl rounded-bl-md bg-down-bg text-down-ink text-[13px] leading-5">
          {{ m.content }}
        </div>

        <div v-else :key="`a${i}`"
          class="self-start max-w-[92%] px-3 py-2.5 rounded-2xl rounded-bl-md bg-surface-3 text-ink text-[13px] leading-5 flex flex-col gap-2">
          <p class="whitespace-pre-wrap break-words">{{ m.content }}</p>
          <div v-if="m.filters.length || m.view" class="flex flex-wrap gap-1.5">
            <span v-for="f in m.filters" :key="f.key"
              class="inline-flex items-center px-2 h-6 rounded-md bg-surface border border-line text-[12px] text-ink-2">
              {{ describeFilter(f) }}
            </span>
            <span v-if="m.view"
              class="inline-flex items-center px-2 h-6 rounded-md bg-surface border border-dashed border-line-strong text-[12px] text-ink-2">
              {{ describeView(m.view) }}
            </span>
          </div>
          <ul v-if="m.unsupported.length" class="text-[12px] leading-4 text-warn bg-warn-bg rounded-lg px-2.5 py-1.5">
            <li class="font-medium">{{ $t("Not supported by the screener:") }}</li>
            <li v-for="u in m.unsupported" :key="u">• {{ u }}</li>
          </ul>
          <p v-if="m.warnings.length" class="text-[11px] leading-4 text-muted" :title="m.warnings.join('\n')">
            {{ $t("assistant-dropped", { count: m.warnings.length }) }}
          </p>
          <div v-if="m.previousQuery && (m.undone || i === lastApplied)" class="flex">
            <button v-if="!m.undone" type="button" class="text-[12px] font-medium text-ink-3 hover:text-ink underline"
              :disabled="loading" @click="undo(m)">
              {{ $t("Undo these filters") }}
            </button>
            <span v-else class="text-[12px] text-faint">{{ $t("Previous filters restored") }}</span>
          </div>
        </div>
      </template>

      <div v-if="loading" class="self-start px-3 py-2.5 rounded-2xl rounded-bl-md bg-surface-3 flex gap-1"
        :aria-label="$t('Thinking…')">
        <span v-for="n in 3" :key="n" class="qp-dot" :style="{ animationDelay: `${(n - 1) * 0.15}s` }"></span>
      </div>
    </div>

    <!-- Input -->
    <form class="shrink-0 border-t border-line-card p-3 flex flex-col gap-2" @submit.prevent="send()">
      <div class="flex items-end gap-2 rounded-xl border border-line focus-within:border-brand bg-surface px-3 py-2">
        <textarea ref="input" v-model="draft" rows="2" :maxlength="maxLength" :disabled="loading"
          :placeholder="$t('assistant-placeholder')" :aria-label="$t('assistant-placeholder')"
          class="flex-1 resize-none outline-none text-[13px] leading-5 text-ink placeholder-faint bg-transparent"
          @keydown.enter.exact.prevent="send()"></textarea>
        <button type="submit" class="qp-btn qp-btn-primary !px-0 w-9 shrink-0" :disabled="loading || !draft.trim()"
          :aria-label="$t('Send')" :title="$t('Send')">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
      <p class="text-[11px] leading-4 text-faint">{{ $t("assistant-disclaimer") }}</p>
    </form>
  </aside>
</template>

<script>
import axios from "../../api/axios";

const MAX_LENGTH = 500;
// Example requests shown in an empty panel: assistant-example-1 ... -N in en.json / fr.json.
const EXAMPLE_COUNT = 8;
// Previous exchanges (request + answer) sent with a request, so follow-ups ("only tech now") make sense.
const HISTORY_EXCHANGES = 3;

export default {
  name: "ScanAssistant",
  props: {
    // () => the filters on screen, as assistant filters.
    currentFilters: { type: Function, required: true },
    // (filters, { sort, rows }) => applies them and runs the scan; returns the state to restore on undo.
    applyFilters: { type: Function, required: true },
    // (state) => restores a previous scan (what applyFilters returned).
    restoreQuery: { type: Function, required: true },
    // (filter) => chip label in the UI language.
    describeFilter: { type: Function, required: true },
    // () => the order and rows per page on screen: { sort: { column, order } | null, rows }.
    currentView: { type: Function, required: true },
    // ({ sort, rows }) => chip label of the order and rows per page, or null.
    describeView: { type: Function, required: true },
    // (message) => the results for questions about them: { results: [row, ...], filters (of the scan that produced
    // them), resultCount, resultStart }, or null while a scan runs.
    currentResults: { type: Function, required: true },
  },
  data() {
    return {
      maxLength: MAX_LENGTH,
      draft: "",
      loading: false,
      // { role: "user", content } | { role: "assistant", content, filters, view, unsupported, warnings, previousQuery, undone }
      // | { role: "assistant", error: true, content }
      messages: [],
      // After "New search" the next request ignores the filters on screen and starts from the defaults.
      fresh: false,
    };
  },
  computed: {
    // Only the latest applied answer can be undone: undoing an older one would skip the later changes.
    lastApplied() {
      for (let i = this.messages.length - 1; i >= 0; i--) if (this.messages[i].previousQuery) return i;
      return -1;
    },
    examples() {
      return Array.from({ length: EXAMPLE_COUNT }, (_, i) => this.$t(`assistant-example-${i + 1}`));
    },
  },
  methods: {
    focusInput() {
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const log = this.$refs.log;
        if (log) log.scrollTop = log.scrollHeight;
      });
    },
    // Answered exchanges only, as request/answer pairs: a failed or empty answer leaves its request out, so the
    // model always gets a user turn first and turns that alternate.
    history() {
      const turns = [];
      this.messages.forEach((m, i) => {
        const request = this.messages[i - 1];
        if (m.role !== "assistant" || m.error || !m.content || request?.role !== "user") return;
        turns.push({ role: "user", content: request.content }, { role: "assistant", content: m.content });
      });
      return turns.slice(-HISTORY_EXCHANGES * 2);
    },
    async send(text) {
      const message = (text ?? this.draft).trim().slice(0, MAX_LENGTH);
      if (!message || this.loading) return;
      const history = this.history();
      this.messages.push({ role: "user", content: message });
      this.draft = "";
      this.loading = true;
      this.scrollToEnd();
      try {
        // After "New search" the order on screen is not carried over either (the rows per page are a preference).
        const current = this.currentView();
        // The rows on screen go along even after "New search": a question can still be about them, so they come with
        // the filters that produced them.
        const screen = this.currentResults(message) || {};
        const { data } = await axios.post("/assistant/scan", {
          message,
          currentFilters: this.fresh ? [] : this.currentFilters(),
          sort: this.fresh ? null : current.sort,
          rows: current.rows,
          results: screen.results,
          resultFilters: screen.filters,
          resultCount: screen.resultCount,
          resultStart: screen.resultStart,
          history,
          locale: this.$i18n.locale,
        });
        // An answer about the results (or to something that is not a screening request) only shows the reply: no
        // chips, no undo, and the scan is left alone.
        const answer = data.mode === "answer";
        const filters = !answer && Array.isArray(data.filters) ? data.filters : [];
        // rows 0 is "all": only null keeps the rows on screen.
        const view = { sort: data.sort || null, rows: data.rows ?? null };
        const reply = {
          role: "assistant",
          content: data.reply || "",
          filters,
          view: null,
          unsupported: answer ? [] : data.unsupported || [],
          warnings: answer ? [] : data.warnings || [],
          previousQuery: null,
          undone: false,
        };
        // A reply without filters or order (nothing expressible) leaves the scan alone too.
        if (!answer && (filters.length || view.sort)) {
          reply.previousQuery = this.applyFilters(filters, view);
          // Kept as data, so the chip follows the UI language like the filter chips.
          if (view.sort || view.rows != null) reply.view = view;
        }
        this.messages.push(reply);
        // An answer applied nothing: after "New search" the next filter request still starts over.
        if (!answer) this.fresh = false;
      } catch (error) {
        const code = error.response?.data?.code;
        const key = `assistant-error-${code || "unavailable"}`;
        this.messages.push({ role: "assistant", error: true, content: this.$te(key) ? this.$t(key) : this.$t("assistant-error-unavailable") });
      } finally {
        this.loading = false;
        this.scrollToEnd();
        this.focusInput();
      }
    },
    undo(message) {
      this.restoreQuery(message.previousQuery);
      message.undone = true;
    },
    reset() {
      this.messages = [];
      this.draft = "";
      this.fresh = true;
      this.focusInput();
    },
  },
};
</script>

<style scoped>
.qp-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: rgb(var(--qp-faint));
  animation: qp-dot 1s infinite ease-in-out;
}

@keyframes qp-dot {
  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
}
</style>
